import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaBook, 
  FaCalendarAlt, 
  FaUsers, 
  FaEye, 
  FaDownload, 
  FaClipboardList,
  FaSpinner,
  FaPlus,
  FaFileAlt,
  FaTimes,
  FaUpload,
  FaEdit,
  FaKey
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config/api';

const HomeworkList = () => {
  const { userType } = useAuth();
  const navigate = useNavigate();
  const [homeworks, setHomeworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedHomework, setExpandedHomework] = useState(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [submissionFile, setSubmissionFile] = useState(null);
  const [submissionLoading, setSubmissionLoading] = useState(false);

  useEffect(() => {
    const fetchHomeworks = async () => {
      try {
        setLoading(true);
        let endpoint;
        
        switch (userType) {
          case 'teacher':
            endpoint = `${config.apiURL}/teacher/homeworks`;
            break;
          case 'student':
            endpoint = `${config.apiURL}/student/homeworks`;
            break;
          case 'guardian':
            endpoint = `${config.apiURL}/guardian/student-homeworks`;
            break;
          default:
            throw new Error('Tipo de usuário inválido');
        }

        const response = await axios.get(endpoint);
        setHomeworks(response.data.homeworks);
      } catch (err) {
        console.error('Erro ao carregar lições:', err);
        setError('Erro ao carregar lições');
      } finally {
        setLoading(false);
      }
    };

    fetchHomeworks();
  }, [userType]);

  const toggleExpand = (homeworkId) => {
    setExpandedHomework(expandedHomework === homeworkId ? null : homeworkId);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Em Aberto':
        return 'bg-blue-100 text-blue-800';
      case 'Concluído':
        return 'bg-yellow-100 text-yellow-800';
      case 'Aprovado':
        return 'bg-green-100 text-green-800';
      case 'Atrasado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTitle = () => {
    switch (userType) {
      case 'teacher':
        return 'Minhas Lições de Casa';
      case 'student':
        return 'Minhas Lições';
      case 'guardian':
        return 'Lições do Filho';
      default:
        return 'Lições de Casa';
    }
  };

  const getDescription = () => {
    switch (userType) {
      case 'teacher':
        return 'Gerencie e acompanhe suas lições de casa';
      case 'student':
        return 'Acompanhe suas lições e organize seus estudos';
      case 'guardian':
        return 'Acompanhe o progresso educacional do seu filho';
      default:
        return 'Lições de casa';
    }
  };

  const handleSubmissionUpload = (homework) => {
    setSelectedHomework(homework);
    setShowSubmissionModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSubmissionFile(file);
    }
  };

  const submitHomework = async () => {
    if (!submissionFile || !selectedHomework) return;

    setSubmissionLoading(true);
    try {
      const formData = new FormData();
      formData.append('submission_file', submissionFile);

      const response = await axios.post(
        `${config.apiURL}/student/submit-homework/${selectedHomework.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data) {
        // Atualizar a lista de lições
        const fetchHomeworks = async () => {
          try {
            let endpoint;
            
            switch (userType) {
              case 'teacher':
                endpoint = `${config.apiURL}/teacher/homeworks`;
                break;
              case 'student':
                endpoint = `${config.apiURL}/student/homeworks`;
                break;
              case 'guardian':
                endpoint = `${config.apiURL}/guardian/student-homeworks`;
                break;
              default:
                return;
            }

            const response = await axios.get(endpoint);
            setHomeworks(response.data.homeworks);
          } catch (err) {
            console.error('Erro ao recarregar lições:', err);
          }
        };

        await fetchHomeworks();
        setShowSubmissionModal(false);
        setSubmissionFile(null);
        setSelectedHomework(null);
        alert('Lição enviada com sucesso!');
      }
    } catch (err) {
      console.error('Erro ao enviar lição:', err);
      alert('Erro ao enviar lição: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmissionLoading(false);
    }
  };

  const closeSubmissionModal = () => {
    setShowSubmissionModal(false);
    setSubmissionFile(null);
    setSelectedHomework(null);
  };

  const handleViewSubmissions = (homeworkId) => {
    navigate(`/homework/${homeworkId}/submissions`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando lições...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FaBook className="text-blue-600" />
            <span>{getTitle()}</span>
          </h1>
          <p className="mt-2 text-gray-600">
            {getDescription()}
          </p>
        </div>

        {/* Quick Action for Teachers */}
        {userType === 'teacher' && (
          <div className="mb-6">
            <Link 
              to="/create-homework"
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 w-fit transition-colors"
            >
              <FaPlus className="w-4 h-4" />
              <span>Criar Nova Lição</span>
            </Link>
          </div>
        )}

        {/* Homework List */}
        {homeworks.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FaBook className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma lição encontrada
            </h3>
            <p className="text-gray-600 mb-4">
              {userType === 'teacher' 
                ? 'Crie sua primeira lição para seus alunos'
                : 'Suas lições aparecerão aqui quando o professor criar uma para você'
              }
            </p>
            {userType === 'teacher' && (
              <Link
                to="/create-homework"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Criar Primeira Lição
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {homeworks.map((homework) => (
              <div key={homework.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {homework.title}
                        </h3>
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                          {homework.subject}
                        </span>
                        {homework.submission && (
                          <span className={`text-xs font-medium px-2.5 py-0.5 rounded ${getStatusColor(homework.submission.status)}`}>
                            {homework.submission.status}
                          </span>
                        )}
                      </div>

                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {homework.description}
                      </p>

                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <FaCalendarAlt className="w-4 h-4" />
                          <span>Vencimento: {formatDate(homework.due_date)}</span>
                        </div>
                        {userType === 'teacher' && homework.students && (
                          <div className="flex items-center space-x-1">
                            <FaUsers className="w-4 h-4" />
                            <span>{homework.students.length} aluno(s)</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-1">
                          <FaFileAlt className="w-4 h-4" />
                          <span>{homework.requires_submission ? 'Requer entrega' : 'Apenas leitura'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => toggleExpand(homework.id)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md transition-colors flex items-center space-x-1"
                      >
                        <FaEye className="w-4 h-4" />
                        <span>{expandedHomework === homework.id ? 'Ocultar' : 'Detalhes'}</span>
                      </button>

                      {homework.homework_file && (
                        <button
                          onClick={() => window.open(homework.homework_file, '_blank')}
                          className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-2 rounded-md transition-colors flex items-center space-x-1"
                        >
                          <FaDownload className="w-4 h-4" />
                          <span>Baixar</span>
                        </button>
                      )}

                      {/* Botão de Gabarito para Professores */}
                      {userType === 'teacher' && homework.answer_key_file && (
                        <button
                          onClick={() => window.open(homework.answer_key_file, '_blank')}
                          className="bg-green-100 hover:bg-green-200 text-green-700 px-3 py-2 rounded-md transition-colors flex items-center space-x-1"
                        >
                          <FaKey className="w-4 h-4" />
                          <span>Gabarito</span>
                        </button>
                      )}

                      {/* Botão de Editar para Professores */}
                      {userType === 'teacher' && (
                        <button
                          onClick={() => navigate(`/edit-homework/${homework.id}`)}
                          className="bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-2 rounded-md transition-colors flex items-center space-x-1"
                        >
                          <FaEdit className="w-4 h-4" />
                          <span>Editar</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {expandedHomework === homework.id && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Informações da Lição */}
                        <div>
                          <h4 className="text-md font-semibold text-gray-900 mb-3">
                            Informações da Lição
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div>
                              <span className="font-medium text-gray-700">Descrição:</span>
                              <p className="text-gray-900 mt-1">{homework.description}</p>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Matéria:</span>
                              <span className="text-gray-900">{homework.subject}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Criada em:</span>
                              <span className="text-gray-900">{formatDate(homework.created_at)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Vencimento:</span>
                              <span className="text-gray-900">{formatDate(homework.due_date)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Requer entrega:</span>
                              <span className="text-gray-900">{homework.requires_submission ? 'Sim' : 'Não'}</span>
                            </div>
                            {homework.answer_key_file && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Gabarito:</span>
                                <button
                                  onClick={() => window.open(homework.answer_key_file, '_blank')}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Baixar Gabarito
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Informações de Submissão */}
                        {homework.submission && (
                          <div>
                            <h4 className="text-md font-semibold text-gray-900 mb-3">
                              Status da Entrega
                            </h4>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Status:</span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(homework.submission.status)}`}>
                                  {homework.submission.status}
                                </span>
                              </div>
                              {homework.submission.submitted_at && (
                                <div className="flex justify-between">
                                  <span className="font-medium text-gray-700">Entregue em:</span>
                                  <span className="text-gray-900">{formatDate(homework.submission.submitted_at)}</span>
                                </div>
                              )}
                              {homework.submission.grade && (
                                <div className="flex justify-between">
                                  <span className="font-medium text-gray-700">Nota:</span>
                                  <span className="text-gray-900">{homework.submission.grade}</span>
                                </div>
                              )}
                              {homework.submission.feedback && (
                                <div>
                                  <span className="font-medium text-gray-700">Feedback:</span>
                                  <p className="text-gray-900 mt-1">{homework.submission.feedback}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Lista de Alunos (apenas para professores) */}
                        {userType === 'teacher' && homework.students && homework.students.length > 0 && (
                          <div className="lg:col-span-2">
                            <h4 className="text-md font-semibold text-gray-900 mb-3">
                              Alunos ({homework.students.length})
                            </h4>
                            <div className="bg-gray-50 rounded-lg p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {homework.students.map((student) => (
                                  <div key={student.id} className="bg-white rounded p-3 text-sm">
                                    <p className="font-medium text-gray-900">{student.name}</p>
                                    <p className="text-gray-600">{student.grade}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Ações */}
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="flex space-x-3">
                          {homework.homework_file && (
                            <button
                              onClick={() => window.open(homework.homework_file, '_blank')}
                              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
                            >
                              <FaDownload className="w-4 h-4" />
                              <span>Baixar Lição</span>
                            </button>
                          )}

                          {userType === 'teacher' && (
                            <button
                              onClick={() => handleViewSubmissions(homework.id)}
                              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
                            >
                              <FaClipboardList className="w-4 h-4" />
                              <span>Ver Entregas</span>
                            </button>
                          )}

                          {userType === 'student' && homework.requires_submission && homework.submission?.status === 'Em Aberto' && (
                            <button
                              onClick={() => handleSubmissionUpload(homework)}
                              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
                            >
                              <FaUpload className="w-4 h-4" />
                              <span>Enviar Solução</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Submissão */}
      {showSubmissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Enviar Solução
              </h3>
              <button
                onClick={closeSubmissionModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Lição: <span className="font-medium">{selectedHomework?.title}</span>
              </p>
              <p className="text-sm text-gray-600">
                Selecione o arquivo com sua solução:
              </p>
            </div>

            <div className="mb-6">
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {submissionFile && (
                <p className="text-sm text-green-600 mt-2">
                  Arquivo selecionado: {submissionFile.name}
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeSubmissionModal}
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={submitHomework}
                disabled={!submissionFile || submissionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {submissionLoading ? (
                  <>
                    <FaSpinner className="animate-spin w-4 h-4" />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <FaUpload className="w-4 h-4" />
                    <span>Enviar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeworkList; 